import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, TrendingUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const SocialMediaROICalculator = () => {
  const [investment, setInvestment] = useState(5000);
  const [revenue, setRevenue] = useState(15000);
  const [roi, setRoi] = useState(0);

  useEffect(() => {
    calculateROI();
  }, [investment, revenue]);

  const calculateROI = () => {
    const profit = revenue - investment;
    const calculatedRoi = (profit / investment) * 100;
    setRoi(parseFloat(calculatedRoi.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/social-media-roi-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Social Media ROI Calculator: Measure Campaign Success | Free Tool</title>
        <meta name="description" content="Calculate your Social Media ROI instantly. Compare with platform benchmarks, get optimization tips, and improve your social media returns." />
        <meta name="keywords" content="social media ROI calculator, social media metrics, campaign performance, marketing returns, social ROI" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Social Media ROI Calculator: Optimize Campaign Returns" />
        <meta property="og:description" content="Calculate your Social Media ROI instantly. Get actionable insights to improve campaign performance and boost returns." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Social Media ROI Calculator: Optimize Campaign Returns" />
        <meta property="twitter:description" content="Calculate your Social Media ROI instantly. Get actionable insights to improve campaign performance and boost returns." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Social Media ROI Calculator",
            "description": "Free calculator to measure and optimize social media marketing returns",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Social ROI",
              "Platform Benchmarks",
              "Optimization Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Social Media ROI",
              "description": "The return on investment from social media marketing efforts, measuring campaign costs against generated revenue."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Social Media Managers, Digital Marketers, Business Owners"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Social Media ROI",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total investment"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input revenue generated"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your ROI and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Social Media ROI Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Investment</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={investment}
                  onChange={(e) => setInvestment(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="5000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Revenue</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={revenue}
                  onChange={(e) => setRevenue(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="15000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {roi}%
            <TrendingUp className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Social Media ROI</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Platform Benchmarks</h2>
        <p className="text-gray-600">
          Your Social Media ROI is {roi}%. This means that for every dollar invested in your social media efforts, you're generating ${(roi / 100 + 1).toFixed(2)} in return.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Platform ROI Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Facebook: 150-200%</li>
          <li>Instagram: 200-300%</li>
          <li>LinkedIn: 250-350%</li>
          <li>Twitter: 100-200%</li>
          <li>TikTok: 250-400%</li>
          <li>Pinterest: 150-250%</li>
          <li>YouTube: 300-500%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">ROI Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Set clear goals</li>
          <li>Create engaging content</li>
          <li>Use targeted advertising</li>
          <li>Analyze performance</li>
          <li>Test different platforms</li>
          <li>Engage with audience</li>
          <li>Maintain consistency</li>
          <li>Leverage influencers</li>
          <li>Monitor competitors</li>
          <li>Optimize timing</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Content Performance Metrics</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Engagement Rate</li>
          <li>Click-Through Rate</li>
          <li>Conversion Rate</li>
          <li>Reach & Impressions</li>
          <li>Share of Voice</li>
          <li>Brand Mentions</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Targeted content: +50% ROI</li>
          <li>Consistent posting: +30% ROI</li>
          <li>Platform optimization: +40% ROI</li>
          <li>Audience engagement: +35% ROI</li>
          <li>Influencer partnerships: +45% ROI</li>
        </ul>
      </div>
    </div>
  );
};

export default SocialMediaROICalculator;
